import axios from 'axios';
import { isNil } from 'lodash';
import moment from 'moment/moment';
import {
  AppointmentData,
  BookingDataState, ClientData, HttpResponse, RequestStatus, RequiredAddress,
} from '../model';
import request from '../infra/request';
import { CARD_TYPES, MedicareCard } from '../model/MedicareCard';
import { DATE_WITH_DASH_WITH_TIME } from '../constants/time-formats';
import {
  getWorkflowBillingResponsibility,
  getPrivateReferrerDetailsConfig,
  getPrivateGpDetailsConfig,
  getPrivateClientDetailsConfig,
} from '../config/privateBooking';
import { isBookingV2Enabled } from '../utils/bookingFeatureFlag';

const validateWorkflowData = (createWorkflowData: BookingDataState): boolean => {
  const { showPhn = true } = getPrivateReferrerDetailsConfig() || {};
  const { showReferrerDetailsPage = true } = getPrivateGpDetailsConfig() || {};
  const { showMobile = true, showEmail = true } = getPrivateClientDetailsConfig() || {};
  const {
    appointment, client, referrer, medicareCard, healthSummary = {},
  } = createWorkflowData;
  if (isNil(appointment) || isNil(client) || isNil(healthSummary)) {
    return false;
  }

  if (!showReferrerDetailsPage && isNil(referrer)) {
    // Referrer data is not required for this instance
  } else if (isNil(referrer)) {
    return false;
  }

  const {
    specialistId, type, date, startTime, aptLength,
  } = appointment as AppointmentData;
  if (isNil(specialistId) || isNil(type) || isNil(date) || isNil(startTime) || isNil(aptLength)) {
    return false;
  }

  const {
    firstName, lastName, dateOfBirth, mobile, email, addressResidential,
  } = client as ClientData;

  const {
    streetAddress, suburb, state, postcode,
  } = addressResidential as RequiredAddress;

  if (isNil(firstName) || isNil(lastName) || isNil(dateOfBirth)
      || (showMobile && isNil(mobile)) || (showEmail && isNil(email)) || isNil(streetAddress)
      || isNil(suburb) || isNil(state) || isNil(postcode)
  ) {
    return false;
  }

  if (medicareCard) {
    const {
      medicareCardNumber, medicareCardIRN, expiry, dva, cardType,
    } = medicareCard as MedicareCard;
    if (cardType === CARD_TYPES.DVA) {
      if (dva?.hasDept) {
        if (isNil(dva)) {
          return false;
        }
        const { hasDept, cardType: dvaCardType, cardNumber: dvaCardNumber } = dva;
        if (isNil(hasDept) || isNil(dvaCardType) || isNil(dvaCardNumber)) {
          return false;
        }
      }
    }

    if (cardType === CARD_TYPES.MEDICARE) {
      if (isNil(medicareCardNumber) || isNil(medicareCardIRN) || isNil(expiry)) {
        return false;
      }
    }
  }

  if (referrer) {
    const {
      firstName: referrerFirstName, lastName: referrerLastName, referrerPHN, email: referrerEmail, phone, referrerStartDate,
    } = referrer;

    if (isNil(referrerFirstName) || isNil(referrerLastName) || (showPhn && isNil(referrerPHN)) || isNil(referrerEmail) || isNil(phone) || isNil(referrerStartDate)) {
      return false;
    }
  }
  return true;
};

const trimStringsDeep = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(trimStringsDeep);
  } if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, trimStringsDeep(v)]),
    );
  } if (typeof obj === 'string') {
    return obj.trim();
  }
  return obj;
};

const transformWorkflowData = (createWorkflowData: BookingDataState) => {
  const data: any = {};
  const {
    appointment, client, medicareCard, referrer, gp, healthSummary, attachments, formId,
  } = createWorkflowData;

  const {
    type, date, startTime, aptLength, specialistId, slotId, startDateTime, endDateTime,
  } = appointment as AppointmentData;
  const startDateMoment = moment(`${date}${startTime}`, DATE_WITH_DASH_WITH_TIME);

  const isV2 = isBookingV2Enabled() && !!slotId;

  data.appointment = {
    appointmentType: type,
    // v2 (PMS slot selected): send the slot's exact ISO times so the booked range matches the
    // free window exactly; otherwise reconstruct from date+startTime (v1 behaviour).
    startDateTime: isV2 && startDateTime ? startDateTime : startDateMoment.format(),
    endDateTime: isV2 && endDateTime ? endDateTime : startDateMoment.clone().add(aptLength, 'minutes').format(),
    billingResponsibility: getWorkflowBillingResponsibility(),
    ...(slotId ? { slotId } : {}),
  };
  data.specialist = { specialistId };

  data.client = {
    ...client,
    medicareCard,
  };

  data.referrer = referrer;

  if (gp) {
    data.gp = gp;
  }

  if (formId) {
    data.formId = formId;
  }

  data.healthSummary = {
    questionAndAnswers: Object.entries(healthSummary || {}).map(([key, value]) => ({
      question: key,
      answer: value,
    })),
  };

  if (attachments) {
    data.healthSummary.attachments = Object.values(attachments).map(({ fileId, fileName }) => ({ fileId, fileName }));
  }
  data.metadata = createWorkflowData.metadata;
  data.consents = [
    {
      name: 'Consents',
      description: 'I confirm that the consumer has consented to a referral to the Amplar Health Digital Guided Therapy program which involves the sharing of their personal and health information with Amplar Health.',
      agreed: true,
    },
    {
      name: 'Consents',
      description: 'I confirm that the consumer consents to receiving phone, email and SMS communications from Amplar Health in order to enrol them into the program',
      agreed: true,
    },
  ];

  return trimStringsDeep(data);
};

export const createWorkflow = async (createWorkflowData: BookingDataState, code: string): Promise<HttpResponse<any>> => {
  const isValid = validateWorkflowData(createWorkflowData) && code === 'X7mP92qL';
  const enablePms = process.env.REACT_APP_ENABLE_PMS === 'true';
  // v2 workflow path only applies to the PMS flow; flag-off keeps current behaviour unchanged.
  let url = '/workflows';
  if (enablePms) {
    url = isBookingV2Enabled() ? '/v2/workflows/pms' : '/workflows/pms';
  }
  if (isValid) {
    try {
      const formattedWorkflowData = transformWorkflowData(createWorkflowData);
      const { status, data: { _embedded } } = await request.post<any>(url, formattedWorkflowData, {
        headers: {
          'X-API-KEY': process.env.REACT_APP_API_KEY || '',
        },
        params: {
          code,
        },
      });

      if (status === 201) {
        return { status: RequestStatus.SUCCESS, data: _embedded };
      }

      return { status: RequestStatus.FAILURE, error: 'Booking failed' };
    } catch (error) {
      // Creating a workflow also books the underlying appointment, so the
      // backend returns 409 (Conflict) when the selected time slot has already
      // been taken. Surface it as a dedicated status so the UI can show a
      // friendly reminder instead of a generic failure message.
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        return { status: RequestStatus.CONFLICT, error: 'The selected time slot is no longer available' };
      }

      return { status: RequestStatus.FAILURE, error: error as string };
    }
  }

  return { status: RequestStatus.FAILURE, error: 'Request validation failed' };
};
